import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const colorConfig = {
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-800/50',
    icon: 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400',
    value: 'text-blue-700 dark:text-blue-300',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  },
  emerald: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    border: 'border-emerald-200 dark:border-emerald-800/50',
    icon: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400',
    value: 'text-emerald-700 dark:text-emerald-300',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  },
  violet: {
    bg: 'bg-violet-50 dark:bg-violet-950/30',
    border: 'border-violet-200 dark:border-violet-800/50',
    icon: 'bg-violet-100 dark:bg-violet-900/50 text-violet-600 dark:text-violet-400',
    value: 'text-violet-700 dark:text-violet-300',
    badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300',
  },
  amber: {
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-800/50',
    icon: 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400',
    value: 'text-amber-700 dark:text-amber-300',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  },
  sky: {
    bg: 'bg-sky-50 dark:bg-sky-950/30',
    border: 'border-sky-200 dark:border-sky-800/50',
    icon: 'bg-sky-100 dark:bg-sky-900/50 text-sky-600 dark:text-sky-400',
    value: 'text-sky-700 dark:text-sky-300',
    badge: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
  },
  rose: {
    bg: 'bg-rose-50 dark:bg-rose-950/30',
    border: 'border-rose-200 dark:border-rose-800/50',
    icon: 'bg-rose-100 dark:bg-rose-900/50 text-rose-600 dark:text-rose-400',
    value: 'text-rose-700 dark:text-rose-300',
    badge: 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300',
  },
  teal: {
    bg: 'bg-teal-50 dark:bg-teal-950/30',
    border: 'border-teal-200 dark:border-teal-800/50',
    icon: 'bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400',
    value: 'text-teal-700 dark:text-teal-300',
    badge: 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300',
  },
  orange: {
    bg: 'bg-orange-50 dark:bg-orange-950/30',
    border: 'border-orange-200 dark:border-orange-800/50',
    icon: 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400',
    value: 'text-orange-700 dark:text-orange-300',
    badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-950/30',
    border: 'border-green-200 dark:border-green-800/50',
    icon: 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400',
    value: 'text-green-700 dark:text-green-300',
    badge: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  },
  indigo: {
    bg: 'bg-indigo-50 dark:bg-indigo-950/30',
    border: 'border-indigo-200 dark:border-indigo-800/50',
    icon: 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400',
    value: 'text-indigo-700 dark:text-indigo-300',
    badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300',
  },
};

// ─── Validation indicator dot ─────────────────────────────────────────────────
const ValidationDot = ({ validationIssue }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!validationIssue) return null;

  const isError = validationIssue?.severity === 'error';
  const isWarning = validationIssue?.severity === 'warning';

  const dotClass = isError
    ? 'bg-red-500'
    : isWarning
    ? 'bg-amber-400' :'bg-blue-400';

  const tooltipClass = isError
    ? 'bg-red-600 text-white'
    : isWarning
    ? 'bg-amber-500 text-white' :'bg-blue-600 text-white';

  const iconName = isError ? 'AlertCircle' : isWarning ? 'AlertTriangle' : 'Info';

  return (
    <div
      className="relative flex-shrink-0"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className={`w-4 h-4 rounded-full ${dotClass} flex items-center justify-center cursor-help`}>
        <Icon name={iconName} size={9} className="text-white" />
      </div>
      {showTooltip && (
        <div className={`absolute bottom-full right-0 mb-1.5 z-50 w-52 text-[10px] leading-snug px-2.5 py-2 rounded-lg shadow-lg ${tooltipClass} pointer-events-none`}>
          {validationIssue?.message}
          <div className={`absolute top-full right-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent ${isError ? 'border-t-red-600' : isWarning ? 'border-t-amber-500' : 'border-t-blue-600'}`} />
        </div>
      )}
    </div>
  );
};

const SnapshotKPICard = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendValue,
  color = 'blue',
  loading = false,
  validationIssue = null,
}) => {
  const cfg = colorConfig?.[color] || colorConfig?.blue;

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-4 animate-pulse">
        <div className="flex items-center justify-between mb-3">
          <div className="w-10 h-10 bg-muted rounded-xl" />
          <div className="w-14 h-5 bg-muted rounded-full" />
        </div>
        <div className="w-24 h-7 bg-muted rounded mb-1.5" />
        <div className="w-32 h-3.5 bg-muted rounded" />
      </div>
    );
  }

  const isNoData = value === null || value === undefined || value === '—' || value === 'N/A';

  // Determine border override based on validation severity
  const hasError = validationIssue?.severity === 'error';
  const hasWarning = validationIssue?.severity === 'warning';
  const borderOverride = hasError
    ? 'border-red-300 dark:border-red-700/60'
    : hasWarning
    ? 'border-amber-300 dark:border-amber-700/60'
    : cfg?.border;

  return (
    <div className={`${cfg?.bg} ${borderOverride} border rounded-2xl p-4 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 group relative`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`${cfg?.icon} rounded-xl p-2.5 transition-transform duration-200 group-hover:scale-110`}>
          <Icon name={icon} size={18} />
        </div>
        <div className="flex items-center gap-1.5">
          {/* Validation indicator */}
          <ValidationDot validationIssue={validationIssue} />
          {/* Trend badge */}
          {trend && trendValue && (
            <div className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full ${
              trend === 'up' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                : trend === 'down'? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :'bg-muted text-muted-foreground'
            }`}>
              <Icon
                name={trend === 'up' ? 'TrendingUp' : trend === 'down' ? 'TrendingDown' : 'Minus'}
                size={10}
              />
              {trendValue}
            </div>
          )}
        </div>
      </div>
      <div className={`text-xl font-bold leading-tight mb-1 ${isNoData ? 'text-muted-foreground' : cfg?.value}`}>
        {isNoData ? 'No Data' : value}
      </div>
      <div className="text-xs font-medium text-muted-foreground leading-tight">{title}</div>
      {subtitle && (
        <div className="text-[11px] text-muted-foreground/60 mt-0.5 truncate">{subtitle}</div>
      )}
    </div>
  );
};

export default SnapshotKPICard;
